import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Check, Copy, ExternalLink, Loader2, ShieldCheck, X } from "lucide-react";

import { API } from "../../App";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";

const SCOPES = ["team", "roster_membership", "schedule_event", "venue", "field", "result"];

const GotSportConnectionCard = ({ connector, connection, token, onChanged }) => {
  const [pairing, setPairing] = useState(null);
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [copied, setCopied] = useState(false);
  const headers = { Authorization: `Bearer ${token}` };

  const loadStatus = useCallback(async () => {
    if (!connection?.connection_id) return;
    try {
      const response = await axios.get(`${API}/connect/connections/${connection.connection_id}/collector-status`, { headers });
      setStatus(response.data);
    } catch (requestError) {
      if (requestError.response?.status !== 404) setError(requestError.response?.data?.detail || "Collector status could not be loaded.");
    }
  // The token is stable for the authenticated screen; including a new headers object would restart polling.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connection?.connection_id, token]);

  useEffect(() => {
    loadStatus();
    if (!connection?.connection_id) return undefined;
    const timer = window.setInterval(loadStatus, 3000);
    return () => window.clearInterval(timer);
  }, [connection?.connection_id, loadStatus]);

  const beginPairing = async () => {
    setBusy(true);
    setError("");
    try {
      let activeConnection = connection;
      if (!activeConnection) {
        const created = await axios.post(`${API}/connect/connections/gotsport`, { display_name: "GotSport" }, { headers });
        activeConnection = created.data;
        await onChanged();
      }
      const response = await axios.post(
        `${API}/connect/connections/${activeConnection.connection_id}/pairings`,
        { scopes: SCOPES },
        { headers },
      );
      setPairing(response.data);
      window.open("https://system.gotsport.com/", "sporthouse-gotsport", "popup,width=1180,height=820,noreferrer");
    } catch (requestError) {
      setError(requestError.response?.data?.detail || "GotSport pairing could not be started.");
    } finally {
      setBusy(false);
    }
  };

  const copyCode = async () => {
    await navigator.clipboard.writeText(pairing.pairing_code);
    setCopied(true);
  };

  const cancelRun = async () => {
    const run = status?.latest_run;
    if (!connection || !run) return;
    setBusy(true);
    try {
      await axios.post(`${API}/connect/connections/${connection.connection_id}/runs/${run.run_id}/cancel`, {}, { headers });
      await loadStatus();
    } catch (requestError) {
      setError(requestError.response?.data?.detail || "Collection could not be cancelled.");
    } finally {
      setBusy(false);
    }
  };

  const pairingState = status?.pairing?.status;
  const run = status?.latest_run;
  const authorizationExpiresAt = status?.pairing?.authorization_expires_at;
  const expired = pairingState === "paired" && authorizationExpiresAt && new Date(authorizationExpiresAt) <= new Date();
  const paired = pairingState === "paired" && !expired;
  const collectorLabel = pairingState === "revoked" ? "revoked" : expired ? "expired" : paired ? "paired" : connection?.status || connector.availability;

  return (
    <article className="rounded-[24px] border border-[#dfe6df] bg-white p-6" data-testid="gotsport-connection-card">
      <div className="flex items-start justify-between gap-4">
        <div><h2 className="text-xl font-semibold text-[#102a27]">{connector.name}</h2><p className="mt-1 text-sm text-[#60706a]">Credentials and reusable sessions stay in your browser.</p></div>
        <Badge className={paired ? "bg-[#eefaf3] text-[#0b654f]" : "bg-[#fff4d8] text-[#7a5711]"}>{collectorLabel}</Badge>
      </div>

      <div className="mt-5 border-t border-[#edf0ec] pt-4 text-sm text-[#60706a]">
        {connection ? `Last successful sync: ${connection.last_successful_run_at ? new Date(connection.last_successful_run_at).toLocaleString() : "Not yet synced"}` : "Available to an approved pilot organization."}
      </div>

      {pairing && (
        <div className="mt-4 rounded-2xl border border-[#cbd7cf] bg-[#f6faf6] p-4">
          <div className="flex items-center gap-2 font-semibold text-[#102a27]"><ShieldCheck className="h-4 w-4" /> Pair the GotSport collector</div>
          <p className="mt-2 text-sm text-[#60706a]">Paste this single-use code into the Sporthouse Connect extension. It expires at {new Date(pairing.expires_at).toLocaleTimeString()}.</p>
          <div className="mt-3 flex items-center gap-2"><code className="min-w-0 flex-1 overflow-hidden text-ellipsis rounded-xl bg-white px-3 py-2 text-xs">{pairing.pairing_code}</code><Button variant="outline" size="sm" onClick={copyCode}>{copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}<span className="sr-only">Copy pairing code</span></Button></div>
        </div>
      )}

      {run && (
        <div className="mt-4 rounded-2xl bg-[#f6f8f6] p-4 text-sm">
          <div className="flex items-center justify-between gap-3"><span className="font-semibold text-[#102a27]">Collection {run.status}</span><span className="text-[#60706a]">{run.counts?.collected || 0} records</span></div>
          {run.status === "collecting" && <div className="mt-3 flex items-center justify-between"><span className="flex items-center gap-2 text-[#60706a]"><Loader2 className="h-4 w-4 animate-spin" />Receiving signed chunks</span><Button variant="outline" size="sm" onClick={cancelRun} disabled={busy}><X className="mr-1 h-4 w-4" />Cancel</Button></div>}
        </div>
      )}

      {error && <p className="mt-4 text-sm text-[#9b352a]" role="alert">{error}</p>}
      <div className="mt-5 flex flex-wrap gap-2">
        <Button onClick={beginPairing} disabled={busy}>{busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}{paired ? "Pair another browser" : connection ? "Start pairing" : "Connect GotSport"}</Button>
        <Button variant="outline" onClick={() => window.open("https://system.gotsport.com/", "sporthouse-gotsport", "popup,width=1180,height=820,noreferrer")}><ExternalLink className="mr-2 h-4 w-4" />Open GotSport</Button>
      </div>
      <p className="mt-3 text-xs leading-5 text-[#718079]">Collection begins only after you sign in on the real GotSport domain and select <strong>Collect preview</strong> in the extension. Sporthouse never receives your password or browser session.</p>
    </article>
  );
};

export default GotSportConnectionCard;
